import { Button, Tabs, Tab, Box, TextField } from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { useNavigate } from 'react-router-dom';

import 'react-phone-input-2/lib/bootstrap.css';
import * as Yup from 'yup';

import EditText from '../../components/editText';
import { showError, showToast } from '../../constants/toasts';
import ManageFaq from '../../features/faqs/ManageFaq';
import { Loader } from '../../helpers';
import MainContainer from '../../layout/MainContainer';
import {
  useLazyGetCmsQuery,
  useUpdateCmsMutation,
  useAddSettingMutation,
  useLazyGetSettingQuery,
} from '../../service/cms';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const ManageCms = () => {
  const navigate = useNavigate();

  const [terms, setTerms] = useState(' ');
  const [aboutUs, setAboutUs] = useState(' ');
  const [privacyPolicy, setPrivacyPolicy] = useState(' ');
  const [cmsId, setCmsId] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneCode, setPhoneCode] = useState('+91');

  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const [getCms] = useLazyGetCmsQuery();
  const [getSetting] = useLazyGetSettingQuery();
  const [updateCms] = useUpdateCmsMutation();
  const [addSetting] = useAddSettingMutation();

  const getSettingData = async () => {
    setLoading(true);
    try {
      const response = await getSetting({}).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        formikFee.setFieldValue('inspectionFee', response?.data?.inspectionFee || '');
        formikFee.setFieldValue('postingFee', response?.data?.postingFee);
      }
      setLoading(false);
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  const getCmsData = async () => {
    setLoading(true);
    try {
      const response = await getCms({}).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        formik.setFieldValue('email', response?.data?.email || '');
        formik.setFieldValue('phone', response?.data?.phone);

        setAboutUs(response?.data?.aboutUs || ' ');

        setPrivacyPolicy(response?.data?.privacyPolicy || ' ');

        setTerms(response?.data?.terms || ' ');

        setPhoneCode(response?.data?.dialCode || '');
        setCmsId(response?.data?._id || '');
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue('phone', phone?.replace(country.dialCode, ''));
    setPhoneCode(country?.dialCode);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      phone: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required('Email is required')
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          'Enter a valid email address'
        ),

      phone: Yup.string()
        .required('Phone number is required')
        .min(6, 'Phone number must be at least 6 characters')
        .max(16, 'Phone number must be at least 16 characters'),
    }),
    onSubmit: async () => {
      formik.setSubmitting(true);

      const body = {
        email: formik.values.email,
        phone: formik.values.phone,
        dialCode: (phoneCode.includes('+') ? '' : '+') + phoneCode,
        terms: terms,
        privacyPolicy: privacyPolicy,

        aboutUs: aboutUs,
      };

      if (!aboutUs?.length) {
        return showError('About us is empty');
      }

      if (!terms?.length) {
        return showError('Terms and conditions is empty');
      }

      if (!privacyPolicy?.length) {
        return showError('Privacy policy is empty');
      }

      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await updateCms({
          body: encryptedBody,
          id: cmsId,
        }).unwrap();
        if (response?.statusCode === 200) {
          showToast(response?.message || 'CMS updated successfully!');
        }
      } catch (error: any) {
        console.error(error, 'error');
        showError(error?.data?.message || '');
      }
    },
  });

  const formikFee = useFormik({
    enableReinitialize: true,
    initialValues: {
      inspectionFee: '',
      postingFee: '',
    },
    validationSchema: Yup.object({
      inspectionFee: Yup.number()
        .typeError('Must be a valid number')
        .min(0, 'Minimum value is 0%')
        .max(99.99, 'Maximum value is 99.99%')
        .required('Inspection fee is required'),
      postingFee: Yup.number()
        .typeError('Must be a valid number')
        .min(0, 'Minimum value is 0%')
        .max(99.99, 'Maximum value is 99.99%')
        .required('Posting fee is required'),
    }),
    onSubmit: async (values: any) => {
      const body = {
        inspectionFee: parseFloat(values.inspectionFee),
        postingFee: parseFloat(values.postingFee),
      };
      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = (await addSetting({ body: encryptedBody })) as any;

        if (response?.data?.statusCode === 200) {
          showToast('Fees updated successfully!');
          getSettingData();
        }
      } catch (error: any) {
        showError(error?.message || 'Something went wrong');
      }
    },
  });

  useEffect(() => {
    if (valueTabs === 5) {
      getSettingData();
    } else {
      getCmsData();
    }
  }, [valueTabs]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1>Manage CMS</h1>
        <p>
          <span onClick={() => navigate('/dashboard')}>Dashboard</span> - Manage CMS
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: '100%' }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Contact Us " {...a11yProps(0)} />
              <Tab label="About Us" {...a11yProps(1)} />
              <Tab label="Privacy Policy" {...a11yProps(2)} />
              <Tab label="Terms And Conditions" {...a11yProps(3)} />
              <Tab label="FAQ" {...a11yProps(4)} />
              <Tab label="Fee" {...a11yProps(5)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <CustomTabPanel value={valueTabs} index={0}>
          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label>Email Address</label>
                <TextField
                  className="text_field"
                  hiddenLabel
                  type={'email'}
                  name="email"
                  variant="outlined"
                  fullWidth
                  placeholder="Email"
                  id="email"
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  onChange={(val) => {
                    if (val.target.value === ' ' || val.target.value === '.') {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{ maxLength: 40 }}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </div>
              <div className="control_group w_50">
                <label>Phone Number</label>

                <PhoneInput
                  enableSearch={true}
                  value={phoneCode + formik.values.phone}
                  country={'us'}
                  inputClass="phoneInput"
                  inputStyle={{ width: '100%' }}
                  buttonClass="phoneBtn"
                  placeholder="Phone Number"
                  onChange={(phone, country) => handleChangePhone(phone, country)}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.phone && formik.errors.phone ? (
                  <h6 className="err_msg">{formik.touched.phone && formik.errors.phone}</h6>
                ) : (
                  ''
                )}
              </div>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <form onSubmit={formik.handleSubmit}>
            <EditText content={aboutUs} setContent={setAboutUs} />
            <div className="form_btn end">
              <Button className="btn btn_secondary" type="submit">
                Save
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <form onSubmit={formik.handleSubmit}>
            <EditText content={privacyPolicy} setContent={setPrivacyPolicy} />
            <div className="form_btn end">
              <Button className="btn btn_secondary" type="submit">
                Save
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={3}>
          <form onSubmit={formik.handleSubmit}>
            <EditText content={terms} setContent={setTerms} />
            <div className="form_btn end">
              <Button className="btn btn_secondary" type="submit">
                Save
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={4}>
          <ManageFaq />
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={5}>
          <form className="form" onSubmit={formikFee.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label>Inspection Fee (%)</label>
                <TextField
                  className="text_field"
                  hiddenLabel
                  type="text"
                  name="inspectionFee"
                  variant="outlined"
                  fullWidth
                  placeholder="Inspection Fee"
                  id="inspectionFee"
                  onBlur={formikFee.handleBlur}
                  value={formikFee.values.inspectionFee}
                  onChange={(val) => {
                    const value = val.target.value;
                    if (/^\d{0,2}(\.\d{0,3})?$/.test(value)) {
                      formikFee.setFieldValue('inspectionFee', value);
                    }
                  }}
                  inputProps={{ maxLength: 6 }}
                  helperText={formikFee.touched.inspectionFee && formikFee.errors.inspectionFee}
                />
              </div>
              <div className="control_group w_50">
                <label>Posting Fee (%)</label>
                <TextField
                  className="text_field"
                  hiddenLabel
                  type="text"
                  name="postingFee"
                  variant="outlined"
                  fullWidth
                  placeholder="Posting Fee"
                  id="postingFee"
                  onBlur={formikFee.handleBlur}
                  value={formikFee.values.postingFee}
                  onChange={(val) => {
                    const value = val.target.value;
                    if (/^\d{0,2}(\.\d{0,3})?$/.test(value)) {
                      formikFee.setFieldValue('postingFee', value);
                    }
                  }}
                  inputProps={{ maxLength: 6 }}
                  helperText={formikFee.touched.postingFee && formikFee.errors.postingFee}
                />
              </div>
            </div>
            <div className="form_btn end">
              <Button className="btn btn_secondary" type="submit">
                Save
              </Button>
            </div>
          </form>
        </CustomTabPanel>
      </div>
    </MainContainer>
  );
};

export default ManageCms;
